import React, { useMemo, useState } from 'react';
import { Badge, Box, Button, Group, Menu, Text } from '@mantine/core';
import {
  IconBaseline,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconGitCompare,
  IconHierarchy2,
  IconHighlight,
  IconRoute,
} from '@tabler/icons-react';
import { GanttBaselineCompareControls } from './GanttBaselineCompareControls';
import type {
  BaselineCompareOption,
  BaselineVarianceSummary,
} from '../../../gantt/baselineCompareUtils';
import { ganttToolbarButtonProps } from './ganttToolbarStyles';

export interface GanttAnalysisMenuProps {
  criticalPathOnly: boolean;
  onCriticalPathOnlyChange: (enabled: boolean) => void;
  highlightCriticalPath: boolean;
  onHighlightCriticalPathChange: (enabled: boolean) => void;
  compareBaselines: boolean;
  onCompareBaselinesChange: (enabled: boolean) => void;
  compareBaselinesDisabled?: boolean;
  compareBaselinesDisabledReason?: string;
  compareBaselineIndex?: number | null;
  onCompareBaselineIndexChange?: (index: number) => void;
  compareBaselineOptions?: BaselineCompareOption[];
  compareVarianceSummary?: BaselineVarianceSummary | null;
  compareVarianceBaselineLabel?: string | null;
  onSetBaseline?: () => void;
  setBaselineDisabled?: boolean;
  setBaselineDisabledReason?: string;
  setBaselineLoading?: boolean;
  viewMode: 'schedule' | 'targets';
  showFullWbs?: boolean;
  onShowFullWbsChange?: (show: boolean) => void;
}

function AnalysisMenuItemBody({
  label,
  description,
}: {
  label: string;
  description?: string;
}) {
  return (
    <>
      <Text size="sm" fw={600}>
        {label}
      </Text>
      {description ? (
        <Text size="xs" c="dimmed" lineClamp={2}>
          {description}
        </Text>
      ) : null}
    </>
  );
}

/** Toolbar popover: critical path, baselines, look-ahead full WBS. */
export function GanttAnalysisMenu({
  criticalPathOnly,
  onCriticalPathOnlyChange,
  highlightCriticalPath,
  onHighlightCriticalPathChange,
  compareBaselines,
  onCompareBaselinesChange,
  compareBaselinesDisabled = false,
  compareBaselinesDisabledReason,
  compareBaselineIndex = null,
  onCompareBaselineIndexChange,
  compareBaselineOptions = [],
  compareVarianceSummary = null,
  compareVarianceBaselineLabel = null,
  onSetBaseline,
  setBaselineDisabled = false,
  setBaselineDisabledReason,
  setBaselineLoading = false,
  viewMode,
  showFullWbs = false,
  onShowFullWbsChange,
}: GanttAnalysisMenuProps) {
  const [opened, setOpened] = useState(false);

  const activeToggleCount = useMemo(() => {
    let count = 0;
    if (criticalPathOnly) count += 1;
    if (highlightCriticalPath) count += 1;
    if (viewMode === 'schedule' && compareBaselines) count += 1;
    if (viewMode === 'targets' && showFullWbs) count += 1;
    return count;
  }, [criticalPathOnly, highlightCriticalPath, compareBaselines, showFullWbs, viewMode]);

  return (
    <Menu
      opened={opened}
      onChange={setOpened}
      shadow="md"
      radius="md"
      width={300}
      position="bottom-start"
      withinPortal
      closeOnItemClick={false}
    >
      <Menu.Target>
        <Button
          {...ganttToolbarButtonProps}
          variant="default"
          type="button"
          leftSection={<IconChartBar size={14} stroke={1.75} />}
          rightSection={
            <Group gap={4} wrap="nowrap" align="center">
              {activeToggleCount > 0 ? (
                <Badge size="xs" circle color="brand" variant="filled">
                  {activeToggleCount}
                </Badge>
              ) : null}
              <IconChevronDown size={14} stroke={1.75} />
            </Group>
          }
          styles={{
            ...ganttToolbarButtonProps.styles,
            root: {
              ...ganttToolbarButtonProps.styles.root,
              ...(activeToggleCount > 0 ? { paddingRight: 8 } : null),
            },
          }}
          onClick={() => setOpened((o) => !o)}
          aria-label={
            activeToggleCount > 0
              ? `Analysis, ${activeToggleCount} option${activeToggleCount === 1 ? '' : 's'} on`
              : 'Analysis'
          }
          aria-expanded={opened}
        >
          Analysis
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconRoute size={14} stroke={1.6} />}
          rightSection={
            criticalPathOnly ? <IconCheck size={16} stroke={2} aria-hidden /> : null
          }
          onClick={() => onCriticalPathOnlyChange(!criticalPathOnly)}
          aria-checked={criticalPathOnly}
          role="menuitemcheckbox"
        >
          <AnalysisMenuItemBody
            label="Critical path only"
            description="Show only critical tasks and their ancestors on the chart."
          />
        </Menu.Item>

        <Menu.Item
          leftSection={<IconHighlight size={14} stroke={1.6} />}
          rightSection={
            highlightCriticalPath ? <IconCheck size={16} stroke={2} aria-hidden /> : null
          }
          onClick={() => onHighlightCriticalPathChange(!highlightCriticalPath)}
          aria-checked={highlightCriticalPath}
          role="menuitemcheckbox"
        >
          <AnalysisMenuItemBody
            label="Highlight critical path"
            description="Tint critical tasks with the critical-path strip color. Other tasks stay visible."
          />
        </Menu.Item>

        {viewMode === 'schedule' ? (
          <>
            {onSetBaseline ? (
              <Menu.Item
                leftSection={<IconBaseline size={14} stroke={1.6} />}
                disabled={setBaselineDisabled || setBaselineLoading}
                title={setBaselineDisabledReason}
                onClick={() => {
                  if (setBaselineDisabled || setBaselineLoading) return;
                  onSetBaseline();
                  setOpened(false);
                }}
              >
                <AnalysisMenuItemBody
                  label={setBaselineLoading ? 'Setting…' : 'Set new baseline'}
                  description={
                    setBaselineDisabledReason ||
                    'Create a new baseline from the schedule saved on the server.'
                  }
                />
              </Menu.Item>
            ) : null}

            <Menu.Item
              leftSection={<IconGitCompare size={14} stroke={1.6} />}
              rightSection={
                compareBaselines ? (
                  <IconCheck size={16} stroke={2} aria-hidden />
                ) : null
              }
              disabled={compareBaselinesDisabled}
              title={compareBaselinesDisabledReason}
              onClick={() => {
                if (compareBaselinesDisabled) return;
                onCompareBaselinesChange(!compareBaselines);
              }}
              aria-checked={compareBaselines}
              role="menuitemcheckbox"
            >
              <AnalysisMenuItemBody
                label="Compare baseline"
                description={
                  compareBaselinesDisabledReason ||
                  'Overlay a saved baseline next to live dates on the chart.'
                }
              />
            </Menu.Item>

            {onCompareBaselineIndexChange && compareBaselines ? (
              <Box px="sm" pb="xs" pt={2} onClick={(e) => e.stopPropagation()}>
                <GanttBaselineCompareControls
                  compact
                  hideToggle
                  enabled={compareBaselines}
                  onEnabledChange={onCompareBaselinesChange}
                  baselineIndex={compareBaselineIndex}
                  onBaselineIndexChange={onCompareBaselineIndexChange}
                  options={compareBaselineOptions}
                  varianceSummary={compareVarianceSummary}
                  varianceBaselineLabel={compareVarianceBaselineLabel}
                />
              </Box>
            ) : null}
          </>
        ) : null}

        {viewMode === 'targets' && onShowFullWbsChange ? (
          <Menu.Item
            leftSection={<IconHierarchy2 size={14} stroke={1.6} />}
            rightSection={
              showFullWbs ? <IconCheck size={16} stroke={2} aria-hidden /> : null
            }
            onClick={() => onShowFullWbsChange(!showFullWbs)}
            aria-checked={showFullWbs}
            role="menuitemcheckbox"
          >
            <AnalysisMenuItemBody
              label="Full WBS"
              description="Show the full work breakdown instead of look-ahead leaf tasks only."
            />
          </Menu.Item>
        ) : null}
      </Menu.Dropdown>
    </Menu>
  );
}
